// What happened in one Remote Punch alert — opened from an alert recipient's
// notification. Read top to bottom: what was suspicious and why it matters,
// who it was (name, company, Infinity Code), what happened to the punch,
// where it was (map), and from which device.
import React, { useEffect, useState } from "react";
import {
  ShieldAlert, Laptop, Copy, Clock, ExternalLink, User, MapPin, Smartphone, CheckCircle2, XCircle,
  Building2, Fingerprint, Navigation, Crosshair, Globe, Activity, Eye,
} from "lucide-react";
import RemotePunchDialog, { dialogColors } from "./RemotePunchDialog";
import PunchMap, { accuracyQuality } from "./PunchMap";
import { rpText } from "./remotePunchText";
import { remotePunchApi, areaNameFor, googleMapsUrl } from "./remotePunchApi";
import { InfoTile, TileGrid, SectionTitle, AccuracyValue, Coordinates, primaryLinkStyle, formatDateTime } from "./RemotePunchParts";

const SIGNAL_ICON = { LAPTOP: Laptop, IDENTICAL_COORDINATES: Copy, TIMEZONE_MISMATCH: Clock };

/** "Chrome · Android" from a user agent — enough to recognise the device. */
export const describeUserAgent = (ua) => {
  if (!ua) return null;
  const browser =
    /edg\//i.test(ua) ? "Edge"
      : /opr\/|opera/i.test(ua) ? "Opera"
        : /samsungbrowser/i.test(ua) ? "Samsung Internet"
          : /chrome|crios/i.test(ua) ? "Chrome"
            : /firefox|fxios/i.test(ua) ? "Firefox"
              : /safari/i.test(ua) ? "Safari"
                : null;
  const os =
    /iphone/i.test(ua) ? "iPhone"
      : /ipad/i.test(ua) ? "iPad"
        : /android/i.test(ua) ? "Android"
          : /windows/i.test(ua) ? "Windows"
            : /mac os x|macintosh/i.test(ua) ? "macOS"
              : /linux/i.test(ua) ? "Linux"
                : null;
  return [browser, os].filter(Boolean).join(" · ") || null;
};

const Skeleton = ({ c, h = 16, w = "100%", r = 8, mb = 10 }) => (
  <div style={{ height: h, width: w, borderRadius: r, marginBottom: mb, background: c.dark ? "rgba(255,255,255,0.06)" : "#eef2f7" }} />
);

export default function RemotePunchAlertModal({ alertId, theme, language, onClose }) {
  const t = rpText(language);
  const dark = theme === "dark";
  const c = dialogColors(dark);
  const [alert, setAlert] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setAlert(null);
    setError(null);
    remotePunchApi
      .getAlert(alertId)
      .then((data) => !cancelled && setAlert(data))
      .catch((err) => !cancelled && setError(err?.message || t.loadFailed));
    return () => { cancelled = true; };
  }, [alertId, t.loadFailed]);

  const name = alert ? (language === "ar" ? alert.employeeNameAr || alert.employeeName : alert.employeeName || alert.employeeNameAr) || alert.infinityCode : "";
  const hasPosition = alert && Number.isFinite(alert.latitude) && Number.isFinite(alert.longitude);
  const area = alert ? areaNameFor(alert, language) : null;
  const device = alert ? describeUserAgent(alert.userAgent) : null;
  const deviceClass = alert?.signals?.find((s) => s.code === "LAPTOP")?.details?.deviceClass
    || (alert?.userAgent && /mobile|iphone|android/i.test(alert.userAgent) ? "mobile" : null);
  const outcomeColor = alert?.accepted ? c.success : c.danger;

  return (
    <RemotePunchDialog
      onClose={onClose}
      title={t.alertTitle}
      subtitle={alert ? formatDateTime(alert.createdAt, language, true) : null}
      icon={ShieldAlert}
      iconColor={c.danger}
      dark={dark}
      language={language}
      maxWidth={680}
      zIndex={10050}
      closeLabel={t.close}
      footer={
        hasPosition && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <a href={googleMapsUrl(alert.latitude, alert.longitude)} target="_blank" rel="noopener noreferrer" style={primaryLinkStyle(c)}>
              <ExternalLink size={15} />
              {t.openInGoogleMaps}
            </a>
          </div>
        )
      }
    >
      {error ? (
        <div style={{ padding: 28, textAlign: "center", color: c.danger, fontWeight: 600 }}>{error}</div>
      ) : !alert ? (
        <div>
          <Skeleton c={c} h={86} r={14} mb={16} />
          <Skeleton c={c} w="40%" />
          <Skeleton c={c} h={56} r={12} />
          <Skeleton c={c} h={200} r={14} />
        </div>
      ) : (
        <>
          {/* What happened — most severe first */}
          <div style={{ borderRadius: 14, border: `1px solid ${c.danger}40`, background: `${c.danger}0f`, overflow: "hidden" }}>
            {(alert.signals || []).map((s, i) => {
              const info = t.signals[s.code] || { title: s.code, body: "" };
              const Icon = SIGNAL_ICON[s.code] || ShieldAlert;
              return (
                <div key={s.code} style={{ display: "flex", gap: 12, padding: "13px 14px", borderTop: i ? `1px solid ${c.danger}26` : "none" }}>
                  <div style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: `${c.danger}1f`, color: c.danger }}>
                    <Icon size={18} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 14.5, color: c.text }}>{info.title}</div>
                    <div style={{ fontSize: 13, color: c.muted, lineHeight: 1.65, marginTop: 2 }}>{info.body}</div>
                    {s.code === "IDENTICAL_COORDINATES" && s.details?.dateTime && (
                      <div style={{ fontSize: 12.5, marginTop: 6, fontWeight: 600 }}>{info.previous}: {formatDateTime(s.details.dateTime, language, true)}</div>
                    )}
                    {s.code === "TIMEZONE_MISMATCH" && s.details?.deviceTimezone && (
                      <div style={{ fontSize: 12.5, marginTop: 6, fontWeight: 600 }}>
                        {t.deviceTimezone}: <span dir="ltr">{s.details.deviceTimezone}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <SectionTitle c={c} icon={User}>{t.alertEmployee}</SectionTitle>
          <TileGrid min={170}>
            <InfoTile c={c} icon={User} label={t.alertEmployee} wide>{name}</InfoTile>
            <InfoTile c={c} icon={Building2} label={t.alertCompany}>{alert.workingCompany || "—"}</InfoTile>
            {alert.clientName && alert.clientName !== alert.workingCompany && (
              <InfoTile c={c} icon={Building2} label={t.alertClient}>{alert.clientName}</InfoTile>
            )}
            <InfoTile c={c} icon={Fingerprint} label="Infinity Code"><span dir="ltr">{alert.infinityCode}</span></InfoTile>
          </TileGrid>

          <SectionTitle c={c} icon={Activity}>{t.thePunch}</SectionTitle>
          <TileGrid min={170}>
            <InfoTile c={c} icon={alert.accepted ? CheckCircle2 : XCircle} accent={outcomeColor} label={alert.punchType === "CHECK_OUT" ? t.checkOut : t.checkIn}>
              <span style={{ color: outcomeColor }}>{alert.accepted ? t.accepted : t.refused}</span>
              {!alert.accepted && alert.rejectCode && (
                <div style={{ fontSize: 12.5, fontWeight: 600, color: c.muted, marginTop: 2 }}>{t.rejectCodes[alert.rejectCode] || alert.rejectCode}</div>
              )}
            </InfoTile>
            <InfoTile c={c} icon={Clock} label={t.time}>{formatDateTime(alert.punchAt || alert.createdAt, language)}</InfoTile>
          </TileGrid>
          {alert.monitoringMode && (
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 10, padding: "10px 13px", borderRadius: 12, background: `${c.warning}12`, border: `1px solid ${c.warning}38`, color: c.warning, fontSize: 12.5, fontWeight: 600, lineHeight: 1.6 }}>
              <Eye size={15} style={{ flexShrink: 0, marginTop: 2 }} />
              <span><b>{t.monitoringMode}:</b> {t.monitoringDaysHint}</span>
            </div>
          )}

          {hasPosition && (
            <>
              <SectionTitle c={c} icon={MapPin}>{t.location}</SectionTitle>
              <PunchMap
                latitude={alert.latitude}
                longitude={alert.longitude}
                accuracyMeters={alert.accuracyMeters}
                dark={dark}
                height={260}
                failedText={t.mapLoadFailed}
                recenterLabel={t.recenter}
                accuracyLabel={t.quality[accuracyQuality(alert.accuracyMeters).level]}
              />
              <div style={{ marginTop: 10 }}>
                <TileGrid min={170}>
                  <InfoTile c={c} icon={Navigation} accent={c.primary} label={t.area} wide>
                    {area || <span style={{ color: c.muted, fontWeight: 500 }}>{alert.eventId ? t.areaPending : "—"}</span>}
                  </InfoTile>
                  {alert.locationName && <InfoTile c={c} icon={Building2} accent={c.success} label={t.office} wide>{alert.locationName}</InfoTile>}
                  {Number.isFinite(alert.accuracyMeters) && (
                    <InfoTile c={c} icon={Crosshair} label={t.accuracy}><AccuracyValue meters={alert.accuracyMeters} t={t} /></InfoTile>
                  )}
                  <InfoTile c={c} icon={MapPin} label={t.coordinates}>
                    <Coordinates latitude={alert.latitude} longitude={alert.longitude} t={t} c={c} />
                  </InfoTile>
                </TileGrid>
              </div>
            </>
          )}

          <SectionTitle c={c} icon={deviceClass === "desktop" ? Laptop : Smartphone}>{t.device}</SectionTitle>
          <TileGrid min={150}>
            {deviceClass && (
              <InfoTile c={c} icon={deviceClass === "desktop" ? Laptop : Smartphone} accent={deviceClass === "desktop" ? c.danger : c.success} label={t.device}>
                {t.deviceClass[deviceClass] || deviceClass}
              </InfoTile>
            )}
            {device && <InfoTile c={c} icon={Globe} label={t.browser}><span dir="ltr">{device}</span></InfoTile>}
            {alert.ipAddress && <InfoTile c={c} icon={Globe} label={t.ipAddress}><span dir="ltr">{alert.ipAddress}</span></InfoTile>}
            {alert.deviceTimezone && <InfoTile c={c} icon={Clock} label={t.deviceTimezone}><span dir="ltr">{alert.deviceTimezone}</span></InfoTile>}
          </TileGrid>

          {alert.otherAlertsToday?.length > 0 && (
            <>
              <SectionTitle c={c} icon={ShieldAlert}>{t.otherToday}</SectionTitle>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {alert.otherAlertsToday.map((o) => (
                  <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10, background: c.soft, border: `1px solid ${c.border}`, fontSize: 13 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: o.accepted ? c.warning : c.danger, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontWeight: 600 }}>{(t.signals[o.topSignal] || { title: o.topSignal }).title}</span>
                    <span style={{ color: o.accepted ? c.success : c.danger, fontWeight: 700 }}>{o.accepted ? t.accepted : t.refused}</span>
                    <span style={{ color: c.muted }}>{formatDateTime(o.createdAt, language)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </RemotePunchDialog>
  );
}
